<script lang="ts" setup>
  import { z } from 'zod'
  import type { FetchError } from 'ofetch'
  import { nameRegex } from '@/constants/regex'
  import { GRILLD_TERMS_GIFT_CARD } from '@/constants/url'
  import { formatPrice } from '@/utils/payment'
  import type { GiftCardOrderResponse, GiftCardRecipientRequest } from '@/types/api/gift-cards'
  import { useSubmitForm } from '../../composables/submit.composable'
  import type { EmitNextStepPaymentPayload } from '../../types/wizard'
  import type { RecipientsSubmitOrderParams } from '../../types/submit'

  const MAX_RECIPIENTS = 50
  const giftCardStore = useGiftCardsStore()

  const emit = defineEmits<{
    (event: 'next-step', payload: EmitNextStepPaymentPayload): void
  }>()

  const validationSchema = toTypedSchema(
    z.object({
      message: z.string().max(250).optional(),
      recipients: z
        .array(
          z.object({
            name: z
              .string()
              .regex(nameRegex, 'Recipient name must only contain letters and spaces')
              .min(1, 'Required')
              .max(64),
            emailAddress: z
              .string()
              .email('The email must be valid and contain an @ in the address')
              .min(1, 'Required')
              .max(254)
          })
        )
        .min(1, { message: 'At least one recipient is required' })
        .max(MAX_RECIPIENTS, { message: 'Maximum of 50 recipients' })
    })
  )

  const {
    handleSubmit,
    values: formValues,
    setFieldValue,
    setFieldTouched,
    meta,
    isSubmitting,
    validateField
  } = useForm({
    validationSchema,
    initialValues: {
      message: giftCardStore.wizard.values.recipients.message || '',
      recipients: giftCardStore.wizard.values.recipients.recipients.toSpliced(
        giftCardStore.wizard.values.recipients.recipients.length
      )
    }
  })

  onMounted(() => {
    formValues.recipients?.forEach((recipient, index) => {
      if (recipient.name) {
        setFieldTouched(`recipients.${index}.name` as any, true)
        validateField(`recipients.${index}.name` as any)
      }
      if (recipient.emailAddress) {
        setFieldTouched(`recipients.${index}.emailAddress` as any, true)
        validateField(`recipients.${index}.emailAddress` as any)
      }
    })
  })

  const isValid = computed(() => meta.value.valid)

  const imageKey = ref(0)

  onMounted(() => {
    // Reload the image when the component is mounted to avoid the image being left in a loading state
    // when the user navigates back to this step
    imageKey.value++
  })

  // If the message is changed, update the store
  watch(
    () => formValues.message,
    (newValue) => {
      if (!newValue) {
        return
      }

      giftCardStore.wizard.values.recipients.message = newValue
    }
  )

  watch(
    () => formValues.recipients,
    (newRecipients) => {
      if (!newRecipients) {
        return
      }

      if (newRecipients.length !== giftCardStore.wizard.values.recipients.recipients.length) {
        giftCardStore.wizard.values.recipients.recipients = [
          ...newRecipients.map((recipient) => ({
            name: recipient.name,
            emailAddress: recipient.emailAddress
          }))
        ]

        giftCardStore.wizard.values.personalisation.quantity = newRecipients.length
      } else {
        // Update only the item in the store that has changed
        const changedIndex = giftCardStore.wizard.values.recipients.recipients.findIndex(
          (recipient, index) =>
            recipient.name !== newRecipients[index].name || recipient.emailAddress !== newRecipients[index].emailAddress
        )

        if (changedIndex !== -1) {
          giftCardStore.wizard.values.recipients.recipients[changedIndex] = {
            name: newRecipients[changedIndex].name,
            emailAddress: newRecipients[changedIndex].emailAddress
          }
        }
      }
    },
    { deep: true }
  )

  // If the quantity is changed (e.g. from Personalisation step),
  // update the form values array to match the new quantity
  watch(
    () => giftCardStore.wizard.values.personalisation.quantity,
    (quantity) => {
      if (!quantity || quantity === formValues.recipients?.length) {
        return
      }

      setFieldValue('recipients', giftCardStore.wizard.values.recipients.recipients.toSpliced(quantity), false)
    }
  )

  const isAlertVisible = ref(false)
  const alertContent = ref({
    header: "Oops! That's an error.",
    message: 'Please check your internet connection and try again.'
  })

  const { executeRecaptcha } = useGoogleRecaptcha()

  function onSubmit() {
    handleSubmit(async () => {
      if (!isValid.value) {
        console.error('Form is invalid. Not submitting.')
        return
      }

      try {
        const validatedBody = giftCardStore.getBodyForSubmissionOnRecipientsStep()

        if (!validatedBody) {
          throw Error('Body is not valid is not defined')
        }

        const body: RecipientsSubmitOrderParams['body'] = {
          ...validatedBody,
          recaptchaResponse: (await executeRecaptcha('submit')) || ''
        }

        const response = await useSubmitForm({
          step: 'recipients',
          body
        })

        if (!response) {
          throw Error('Failed to submit form', response)
        }

        // Move to step 4 (Payment Step)
        emit('next-step', { step: 'payment', paymentDetails: response })
      } catch (error: unknown) {
        console.log(error as FetchError<GiftCardOrderResponse>)
        isAlertVisible.value = true
      }
    })()
  }

  const { push, remove, fields } = useFieldArray<{
    name: string
    emailAddress: string
  }>('recipients')

  async function addRecipient() {
    const defaultRecipient: GiftCardRecipientRequest = {
      name: '',
      emailAddress: ''
    }

    const recipientsLength = formValues.recipients?.length || 0
    const storeRecipientsLength = giftCardStore.wizard.values.recipients.recipients.length

    // If a previously enter recipient exists in the store list (i.e. the length of the store list is greater tha the length of the form list),
    // push it with those values.
    // Else, push a new recipient with default values
    const newRecipient =
      storeRecipientsLength > recipientsLength
        ? giftCardStore.wizard.values.recipients.recipients[recipientsLength]
        : defaultRecipient

    push(newRecipient)

    await nextTick()

    // Validate the new recipient fields if they are prefilled
    if (newRecipient.name || newRecipient.emailAddress) {
      validateField(`recipients.${recipientsLength}.name` as any)
      validateField(`recipients.${recipientsLength}.emailAddress` as any)
    }

    // Focus on and scoll to the new recipient's name field
    const recipientNameField = document.querySelector('input[id="recipients.' + recipientsLength + '.name"]')

    if (recipientNameField) {
      ;(recipientNameField as HTMLInputElement).focus()
      recipientNameField.scrollIntoView({ behavior: 'smooth' })
    }
  }

  function removeRecipient(index: number) {
    remove(index)
  }
</script>

<template>
  <div class="c-gift-card-personalise gift-card-step-panel">
    <div class="step-header-content">
      <span class="text-button-medium">Grill'd gift cards</span>
      <h2 class="header-3">A Gift to Savour</h2>
    </div>

    <div class="body-content">
      <div class="col-1">
        <div class="gift-card-image-block">
          <CLazyImage
            v-if="giftCardStore.wizard.values.skin?.url"
            :key="imageKey"
            :src="giftCardStore.wizard.values.skin.url"
            alt=""
            aspect-ratio="1.8"
            border-radius="4px"
            fit="cover"
          />
          <p class="body">
            Email a gift card with a personal message. Valid for 3 years, redeemable online or at Grill’d restaurants.
          </p>
        </div>

        <div class="gift-card-summary text-button-medium">
          <div class="gift-card-summary-row">
            <span>Gift card amount</span>
            <span>
              {{
                !giftCardStore.wizard.values.personalisation.customAmount
                  ? giftCardStore.wizard.values.personalisation.presetAmount
                    ? formatPrice(giftCardStore.wizard.values.personalisation.presetAmount)
                    : '-'
                  : formatPrice(giftCardStore.wizard.values.personalisation.customAmount * 100)
              }}
            </span>
          </div>
          <div class="gift-card-summary-row">
            <span>Gift card quantity</span>
            <span>
              {{
                giftCardStore.wizard.values.recipients.recipients.length ||
                giftCardStore.wizard.values.personalisation.quantity ||
                '-'
              }}
            </span>
          </div>
          <div class="gift-card-summary-row">
            <span>Send later?</span>
            <span>{{ giftCardStore.wizard.values.personalisation.scheduledDateTime ? 'Yes' : 'No' }}</span>
          </div>
        </div>
      </div>

      <div class="col-2">
        <form @submit.prevent="onSubmit">
          <fieldset class="form-group">
            <div class="form-group-header mb-1">
              <h2 class="header-5">Note to all</h2>
              <p class="body">Every recipient will receive the same personalised message.</p>

              <CTextareaField
                :helper-text="`${formValues.message ? formValues.message.length : 0}/250 ⋅ Optional`"
                :maxlength="250"
                label="Tell them why they deserve it"
                name="message"
                validate-initial-value
              />
            </div>
          </fieldset>

          <div class="form-group">
            <div class="form-group-header">
              <h2 class="header-5">Recipients</h2>
              <p class="body mb-1">Name the lucky people getting each gift card.</p>
            </div>

            <div class="form-group-body">
              <template v-for="(field, idx) in fields" :key="field.key">
                <CGiftCardRecipientItem
                  :count-label="`${idx + 1}/${fields.length}`"
                  :index="idx"
                  :is-removable="fields.length > 1"
                  @remove="removeRecipient($event)"
                />

                <Divider />
              </template>

              <CButton
                v-if="formValues.recipients && formValues.recipients.length < MAX_RECIPIENTS"
                class="add-recipient-button"
                type="button"
                variant="secondary"
                @click.stop.prevent="addRecipient"
              >
                Add new gift card recipient
              </CButton>
            </div>
          </div>

          <div>
            <CLoadingButton :disabled="!isValid" :loading="isSubmitting" fluid type="submit" @click="onSubmit">
              Confirm ⋅ {{ giftCardStore.wizard.values.personalisation.quantity }} gift cards
            </CLoadingButton>

            <p class="terms-and-conditions-text body-small">
              Valid for 3 years.
              <a :href="GRILLD_TERMS_GIFT_CARD" target="_blank">Full Terms & Conditions</a>
            </p>
          </div>
        </form>
      </div>
    </div>
  </div>

  <Dialog v-model:visible="isAlertVisible" :closable="false" :draggable="false" :header="alertContent.header" modal>
    <p class="body">
      {{ alertContent.message }}
    </p>
    <template #footer>
      <CButton fluid variant="primary" @click="isAlertVisible = false">Gotcha</CButton>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
  @import '../../styles/step.scss';

  .col-1 {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .gift-card-summary {
      display: flex;
      padding: 20px 16px;
      flex-direction: column;
      gap: 10px;
      background-color: var(--color-secondary-paper-light);
      border-radius: 8px;

      .gift-card-summary-row {
        display: flex;
        justify-content: space-between;
        gap: 24px;
        color: var(--color-text-muted);
      }
    }
  }
</style>
